import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Input from '@material-ui/core/Input';

import hexToRgb from 'styles/hexToRgb';

const fontSize = '1.1vw';
const letterSpacing = '0.008em';
const lineHeight = '1.2';
const fontFamily = 'Times, "Times New Roman", serif';

const styles = theme => ({
  root: {
    position: 'absolute',
    zIndex: 10,
  },
  inputRoot: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    alignItems: 'flex-start',
  },
  inputInput: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    padding: 0,
    height: '100%',
    fontSize,
    lineHeight,
    letterSpacing,
    fontFamily,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    resize: 'none',
    backgroundColor: 'transparent',
    zIndex: 1,
  },
  inputInputTextAlignRight: {
    textAlign: 'right',
  },
  inputInputTextAlignCenter: {
    textAlign: 'center',
  },
  fakeValue: {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: 0,
    fontSize,
    lineHeight,
    letterSpacing,
    fontFamily,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },
  fakeValueMultiline: {
    width: '100%',
    overflowWrap: 'break-word',
    wordWrap: 'breakWord',
  },
  inputBackground: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    padding: 0,
    backgroundColor: `rgba(${hexToRgb(theme.palette.grey[900])}, 0.05)`,
    opacity: 0.5,
  },
});

const Textarea = ({ inputRef, ...otherProps }) => (
  <textarea
    {...otherProps}
    ref={inputRef}
  />
);

Textarea.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

class DocumentPreviewFillTextField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fakeValue: props.input.value.toString() || '',
      selectionStart: null,
      threshold: 2,
    };

    this.onChange = this.onChange.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { input, multiline } = this.props;
    const { fakeValue, selectionStart, threshold } = this.state;
    if (fakeValue !== prevState.fakeValue
      && this.rootRef && this.fakeValueRef && this.inputRef) {
      if (multiline) {
        const rootHeight = this.rootRef.getBoundingClientRect().height;
        const fakeValueHeight = this.fakeValueRef.getBoundingClientRect().height;
        if (fakeValueHeight <= rootHeight) {
          input.onChange(fakeValue);
          this.inputRef.setSelectionRange(selectionStart, selectionStart);
        } else {
          // eslint-disable-next-line
          this.setState({ fakeValue: fakeValue.slice(0, -1) });
        }
      } else {
        const rootWidth = this.rootRef.getBoundingClientRect().width;
        const fakeValueWidth = this.fakeValueRef.getBoundingClientRect().width;
        if (fakeValueWidth + threshold < rootWidth) {
          input.onChange(fakeValue);
          this.inputRef.setSelectionRange(selectionStart, selectionStart);
        } else {
          // eslint-disable-next-line
          this.setState({ fakeValue: fakeValue.slice(0, -1) });
        }
      }
    }
  }

  onChange(event) {
    this.setState({
      fakeValue: event.target.value,
      selectionStart: event.target.selectionStart,
    });
  }

  render() {
    const {
      input,
      x,
      y,
      width,
      height,
      type,
      maxLength,
      multiline,
      textAlign,
      documentWidth,
      documentPrintWidth,
      documentPrintHeight,
      fieldsShift,
      disabled,
      classes,
    } = this.props;
    const { fakeValue } = this.state;
    let fakeValueLines;
    if (fakeValue.indexOf('\n') !== -1) {
      fakeValueLines = fakeValue.split('\n');
    }
    return (
      <div
        className={classes.root}
        ref={(ref) => { this.rootRef = ref; }}
        style={{
          left: `${((x + fieldsShift.x) / documentPrintWidth) * 100}%`,
          top: `${((y + fieldsShift.y) / documentPrintHeight) * 100}%`,
          ...width ? { width: `${((width / documentPrintWidth) * 100)}%` } : {},
          ...height ? { height: `${(height / documentPrintHeight) * 100}%` } : {},
        }}
      >
        <Input
          classes={{
            root: classes.inputRoot,
            input: classNames(classes.inputInput, {
              [classes.inputInputTextAlignRight]: textAlign === 'right',
              [classes.inputInputTextAlignCenter]: textAlign === 'center',
            }),
          }}
          {...input}
          inputRef={(inputRef) => { this.inputRef = inputRef; }}
          onChange={this.onChange}
          type={type}
          disableUnderline
          inputProps={{
            maxLength,
            autoComplete: 'off',
            style: {
              top: (2 / 2.834646) * (documentWidth / documentPrintWidth),
              fontSize: (10 / 2.834646) * (documentWidth / documentPrintWidth),
            },
            disabled,
          }}
          {...multiline ? { inputComponent: Textarea } : {}}
        />
        <div
          className={classes.inputBackground}
          style={{
            top: (3 / 2.834646) * (documentWidth / documentPrintWidth),
          }}
        />
        <div
          className={classNames(classes.fakeValue, {
            [classes.fakeValueMultiline]: multiline,
          })}
          ref={(ref) => { this.fakeValueRef = ref; }}
          style={{
            top: (3 / 2.834646) * (documentWidth / documentPrintWidth),
            fontSize: (10 / 2.834646) * (documentWidth / documentPrintWidth),
          }}
        >
          {fakeValueLines ? (
            fakeValueLines.map((line, index) => (
              // eslint-disable-next-line
              <div key={index}>
                {line.length > 0 ? line.replace(/ {2}/g, '\u00a0 ') : '.'}
              </div>
            ))
          ) : (
            fakeValue.replace(/ {2}/g, '\u00a0 ')
          )}
        </div>
      </div>
    );
  }
}

DocumentPreviewFillTextField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
  }).isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number,
  type: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  textAlign: PropTypes.oneOf(['left', 'right', 'center']),
  multiline: PropTypes.bool,
  documentWidth: PropTypes.number.isRequired,
  documentPrintWidth: PropTypes.number.isRequired,
  documentPrintHeight: PropTypes.number.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    inputRoot: PropTypes.string.isRequired,
    inputInput: PropTypes.string.isRequired,
    inputInputTextAlignRight: PropTypes.string.isRequired,
    inputInputTextAlignCenter: PropTypes.string.isRequired,
    fakeValue: PropTypes.string.isRequired,
    fakeValueMultiline: PropTypes.string.isRequired,
    inputBackground: PropTypes.string.isRequired,
  }).isRequired,
};

DocumentPreviewFillTextField.defaultProps = {
  maxLength: null,
  multiline: false,
  height: 4,
  textAlign: 'left',
};

export default withStyles(styles)(DocumentPreviewFillTextField);



// WEBPACK FOOTER //
// ./src/components/common/DocumentPreviewFillForm/DocumentPreviewFillTextField.js